/**
 * Feature Flags
 *
 * Centralized feature flag configuration. All feature flag reads should go
 * through this module - no component should read import.meta.env directly.
 *
 * Usage:
 * ```ts
 * import { flags, FeatureFlagGuard } from '@/config/featureFlags';
 *
 * // Check flag value
 * if (flags.enableDevPages) { ... }
 *
 * // Guard a route or component
 * <FeatureFlagGuard flag="enableDevPages" fallback={<NotFound />}>
 *   <DevOnlyComponent />
 * </FeatureFlagGuard>
 * ```
 *
 * Environment variables:
 * - VITE_ENABLE_DEV_PAGES: Enable dev/reference pages
 * - VITE_ENABLE_BILLING: Enable billing page
 * - VITE_ENABLE_DIRECT_MAIL: Enable Direct Mail Studio
 * - VITE_ENABLE_CHANNEL_UNIFICATION: Enable channel unification redirect
 * - VITE_ENABLE_DECISION_FRAME_UI: Enable two-mode NextMove decision-frame card render
 * - VITE_ENABLE_CAMPAIGN_WIZARD: Show campaign wizard entry points (default on; set false to hide)
 */

import { ReactNode } from "react";

// =============================================================================
// FLAG DEFINITIONS
// =============================================================================

/**
 * All feature flags with their default values.
 * Defaults are for production - dev pages and billing are OFF by default.
 */
export const flags = {
  /**
   * Enable dev/reference pages (/style-guide, /dev/*, /dashboard, /test-intake-upload)
   * Set VITE_ENABLE_DEV_PAGES=true in .env to enable
   */
  enableDevPages: import.meta.env.VITE_ENABLE_DEV_PAGES === "true",

  /**
   * Enable billing page (/settings/billing)
   * Currently suppressed - set VITE_ENABLE_BILLING=true to enable when ready
   */
  enableBilling: import.meta.env.VITE_ENABLE_BILLING === "true",

  /**
   * Enable Direct Mail Studio (/w/:clientSlug/direct-mail)
   * Set VITE_ENABLE_DIRECT_MAIL=true in .env to enable
   */
  enableDirectMailStudio: import.meta.env.VITE_ENABLE_DIRECT_MAIL === "true",

  /**
   * Enable channel unification (Sprint 3A).
   * When enabled, the Direct Mail page redirects to the unified
   * campaigns view filtered to direct_mail channel.
   * Set VITE_ENABLE_CHANNEL_UNIFICATION=true in .env to enable.
   */
  enableChannelUnification:
    import.meta.env.VITE_ENABLE_CHANNEL_UNIFICATION === "true",

  /**
   * Enable eager preparation of Prepared Actions after adjudication runs
   * for the selected candidate so the review surface is precomputed by the
   * time the user clicks "Review draft" in the queue.
   *
   * DEFAULT OFF. Cost guardrail: each adjudication invocation calls the LLM
   * gateway. Enable per-client only after observing usage patterns.
   *
   * Prerequisite: ScoredCandidate must carry a stable signature, otherwise
   * the chip's findLiveBySignature lookup won't find the precomputed PA.
   * See src/services/domains/preparedActions/eagerPrepHook.ts for details.
   *
   * Set VITE_ENABLE_EAGER_PREP=true in .env to enable.
   */
  enableEagerPrep: import.meta.env.VITE_ENABLE_EAGER_PREP === "true",

  /**
   * Enable the two-mode NextMove decision-frame card render
   * (`canCloseDecision()` gate + Mode 1 exploratory / Mode 2 decisive).
   *
   * DEFAULT ON (Slice 5 flip, 2026-05-05). Every Phase A row fails the
   * gate today (move_type null, tradeoff_source null, tradeoff_text null)
   * so real users see Mode 1 (exploratory) on every queue card. Mode 2
   * becomes reachable in production when the Phase B emitter ships
   * per-candidate recommendationType distinction.
   *
   * Spec: docs/superpowers/specs/2026-05-04-nextmove-decision-closure-design.md
   *
   * Set VITE_ENABLE_DECISION_FRAME_UI=false in .env to explicitly disable.
   */
  enableDecisionFrameUI:
    import.meta.env.VITE_ENABLE_DECISION_FRAME_UI !== "false",

  /**
   * Enable asset manifest generation at publish (Asset Manifest arc, PR #2b).
   * DEFAULT OFF. When enabled, publishing a campaign also projects + persists an
   * AssetManifest (non-blocking — a failure surfaces as a warning and never fails
   * the publish). Set VITE_ENABLE_ASSET_MANIFEST=true in .env to enable.
   */
  enableAssetManifest: import.meta.env.VITE_ENABLE_ASSET_MANIFEST === "true",

  /**
   * Show entry points into the campaign wizard/builder: New Campaign, Edit
   * Campaign and Blueprints. DEFAULT ON. The wizard is being sunset in favor
   * of the claude.ai track15-creative-brief skill (2026-10-23). Set
   * VITE_ENABLE_CAMPAIGN_WIZARD=false to hide the doorways. Routes, code and
   * data are untouched, so direct URLs still work.
   */
  enableCampaignWizard: import.meta.env.VITE_ENABLE_CAMPAIGN_WIZARD !== "false",
} as const;

export type FeatureFlagKey = keyof typeof flags;

// =============================================================================
// FLAG GUARD COMPONENT
// =============================================================================

export interface FeatureFlagGuardProps {
  /** The feature flag to check */
  flag: FeatureFlagKey;
  /** Content to render when flag is enabled */
  children: ReactNode;
  /** Content to render when flag is disabled (defaults to null) */
  fallback?: ReactNode;
}

/**
 * Guard component that only renders children when flag is enabled.
 *
 * @example
 * <FeatureFlagGuard flag="enableDevPages" fallback={<Navigate to="/home" />}>
 *   <StyleGuidePage />
 * </FeatureFlagGuard>
 */
export function FeatureFlagGuard({
  flag,
  children,
  fallback = null,
}: FeatureFlagGuardProps): ReactNode {
  return flags[flag] ? children : fallback;
}

// =============================================================================
// UTILITIES
// =============================================================================

/**
 * Check if a feature flag is enabled.
 * Prefer using the `flags` object directly for better type inference.
 */
export function isEnabled(flag: FeatureFlagKey): boolean {
  return flags[flag];
}

/**
 * Get all enabled flags (useful for debugging/logging).
 */
export function getEnabledFlags(): FeatureFlagKey[] {
  return (Object.keys(flags) as FeatureFlagKey[]).filter((key) => flags[key]);
}

/**
 * Get all disabled flags (useful for debugging/logging).
 */
export function getDisabledFlags(): FeatureFlagKey[] {
  return (Object.keys(flags) as FeatureFlagKey[]).filter((key) => !flags[key]);
}
